import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'crewbit_portal_checks';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface CheckEntry {
  items: Record<number, boolean>;
  checked_at: string;
}
type CheckState = Record<string, CheckEntry>;

function loadState(): CheckState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as CheckState;
  } catch { return {}; }
}

function saveState(state: CheckState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function cleanupOld(state: CheckState): CheckState {
  const now = Date.now();
  const cleaned: CheckState = {};
  for (const [jobId, entry] of Object.entries(state)) {
    if (now - new Date(entry.checked_at).getTime() < MAX_AGE_MS) {
      cleaned[jobId] = entry;
    }
  }
  return cleaned;
}

export function usePortalChecklist() {
  const [state, setState] = useState<CheckState>(() => {
    const loaded = loadState();
    const cleaned = cleanupOld(loaded);
    if (Object.keys(loaded).length !== Object.keys(cleaned).length) saveState(cleaned);
    return cleaned;
  });

  useEffect(() => { saveState(state); }, [state]);

  const getCheckedItems = useCallback((jobId: string): Record<number, boolean> => {
    return state[jobId]?.items ?? {};
  }, [state]);

  const toggleItem = useCallback((jobId: string, index: number): void => {
    setState(prev => {
      const entry = prev[jobId] ?? { items: {}, checked_at: new Date().toISOString() };
      const items = { ...entry.items };
      if (items[index]) { delete items[index]; } else { items[index] = true; }
      return { ...prev, [jobId]: { items, checked_at: new Date().toISOString() } };
    });
  }, []);

  const getCheckedCount = useCallback((jobId: string, total: number): number => {
    const items = state[jobId]?.items ?? {};
    return Object.keys(items).filter(k => items[Number(k)] && Number(k) < total).length;
  }, [state]);

  const isComplete = useCallback((jobId: string, total: number): boolean => {
    if (total === 0) return false;
    return getCheckedCount(jobId, total) === total;
  }, [getCheckedCount]);

  return { getCheckedItems, toggleItem, getCheckedCount, isComplete };
}
